/**
 * Árvores de comportamento das quatro posições (item 5.1; GDD, seção 5). O nível do bot liga ou desliga ramos
 * (passe, jogo em equipe, habilidade, cuidado com faltas) e muda a pontaria e a previsão.
 */
import { ABILITIES, BALL, FLIGHT, FOULS, PITCH, hoopCenters, type Position } from "@besenliga/shared";
import { BUTTON, DEFENDS, type AthleteState, type Vec3 } from "@besenliga/sim";
import { action, attempt, condition, selector, sequence, type BtNode } from "./bt.js";
import {
  attackSide,
  ballAhead,
  byRole,
  closest,
  dist,
  facing,
  flat,
  goalOf,
  hasBall,
  holderOf,
  mates,
  openMate,
  opponents,
  pressure,
  v3,
  type Ctx,
} from "./context.js";

const go = (c: Ctx, target: Vec3, opts: { arrive?: boolean; turbo?: boolean } = {}) => {
  c.intent.target = target;
  c.intent.arrive = opts.arrive ?? false;
  c.intent.turbo = opts.turbo ?? false;
};
const tap = (c: Ctx, bits: number) => {
  c.intent.tap |= bits;
};
const ownSide = (a: AthleteState) => DEFENDS[a.team];
const clampZ = (z: number, m: number) => Math.max(-m, Math.min(m, z));

/** Ramo comum: usar a habilidade quando o nível deixa e a condição da posição aparece. */
const ability = (name: string, when: (c: Ctx) => boolean): BtNode<Ctx> =>
  sequence(
    name,
    condition("sabe usar habilidade", (c) => c.p.abilities && c.me.abilityCooldown === 0),
    condition(name, when),
    action(name, (c) => tap(c, BUTTON.ability)),
  );

/** Petardo caçando chegando perto (para a Finta). */
const petardoClose = (c: Ctx, r: number) =>
  (c.w.balls?.petardos ?? []).some((p) => p.mode === "caca" && dist(p.position, c.me.position) < r);

// ---------------------------------------------------------------- artilheiro

const ARTILHEIRO: BtNode<Ctx> = selector(
  "artilheiro",
  // Com a bola.
  sequence(
    "com a bola",
    condition("está com a Pelota", hasBall),
    selector(
      "o que fazer com a bola",
      ability("finta no Petardo", (c) => petardoClose(c, 6)),
      sequence(
        "passar sob pressão",
        condition("sabe passar", (c) => c.p.passes),
        condition(
          "pressionado",
          (c) => pressure(c, c.me) < 5 && flat(c.me.position, goalOf(attackSide(c.me))) > 20,
        ),
        attempt("passe para o livre", (c) => {
          const m = openMate(c);
          if (!m) return false;
          go(c, m.position);
          if (facing(c.me, m.position) > 0.8) tap(c, BUTTON.action);
          return true;
        }),
      ),
      sequence(
        "arremessar",
        condition("perto do aro", (c) => dist(c.me.position, goalOf(attackSide(c.me))) < c.p.shootDistance),
        action("carrega e arremessa", (c) => {
          const goal = goalOf(attackSide(c.me));
          // Mira num aro: o do meio, ou o do lado mais livre do goleiro.
          const keeper = byRole(opponents(c), "goleiro")[0];
          const hoops = hoopCenters(attackSide(c.me));
          const aim =
            c.p.teamwork && keeper
              ? (hoops.find((h) => Math.abs(h.z - keeper.position.z) > 5) ?? goal)
              : goal;
          go(c, aim);
          if (facing(c.me, aim) > 0.9 && c.me.chargeTicks === 0)
            c.intent.charge = Math.round(30 * c.p.chargeFraction);
        }),
      ),
      action("leva ao aro", (c) => {
        const goal = goalOf(attackSide(c.me));
        go(c, v3(goal.x - attackSide(c.me) * 14, goal.y, goal.z), { turbo: true });
      }),
    ),
  ),
  // Companheiro com a bola: abre espaço à frente dele.
  sequence(
    "companheiro com a bola",
    condition("companheiro com a Pelota", (c) => holderOf(c)?.team === c.me.team),
    action("abre para receber", (c) => {
      const h = holderOf(c);
      if (!h) return;
      const side = attackSide(c.me);
      if (!c.p.teamwork) {
        go(c, h.position);
        return;
      }
      const lane = c.me.id < h.id ? -14 : 14;
      go(c, v3(Math.max(-60, Math.min(60, h.position.x + side * 16)), 14, clampZ(lane, 30)), {
        arrive: true,
        turbo: true,
      });
    }),
  ),
  // Bola com o adversário ou solta.
  selector(
    "sem a bola",
    sequence(
      "um vai na bola",
      condition("sou o mais perto (ou não sei dividir)", (c) => {
        if (!c.p.teamwork) return true;
        const target = ballAhead(c, c.p.leadSeconds);
        return closest(byRole([c.me, ...mates(c)], "artilheiro"), target)?.id === c.me.id;
      }),
      action("persegue a Pelota", (c) => go(c, ballAhead(c, c.p.leadSeconds), { turbo: true })),
    ),
    action("cobre o meio", (c) => {
      // Os outros fecham o caminho entre a bola e o próprio aro.
      const b = ballAhead(c, 0);
      const own = goalOf(ownSide(c.me));
      const team = byRole([c.me, ...mates(c)], "artilheiro").sort((x, y) => (x.id < y.id ? -1 : 1));
      const i = team.findIndex((a) => a.id === c.me.id);
      const k = i % 2 === 0 ? 0.35 : 0.6;
      go(c, v3(b.x + (own.x - b.x) * k, 14, clampZ(b.z * (1 - k) + (i % 2 === 0 ? -8 : 8), 30)), {
        arrive: true,
      });
    }),
  ),
);

// ---------------------------------------------------------------- goleiro

const GOLEIRO: BtNode<Ctx> = selector(
  "goleiro",
  sequence(
    "repõe a bola",
    condition("está com a Pelota", hasBall),
    action("passa para fora", (c) => {
      const m = c.p.passes ? openMate(c) : undefined;
      const target = m?.position ?? v3(0, 14, c.me.position.z);
      // Só vira para o alvo (um passo curto, sem sair da área) e passa quando está de frente.
      const d = flat(target, c.me.position) || 1;
      go(
        c,
        v3(
          c.me.position.x + (target.x - c.me.position.x) / d,
          c.me.position.y,
          c.me.position.z + (target.z - c.me.position.z) / d,
        ),
        {
          arrive: true,
        },
      );
      if (facing(c.me, target) > 0.7 || !c.p.passes) tap(c, BUTTON.action);
    }),
  ),
  ability("Muralha no arremesso", (c) => {
    const b = c.w.balls?.pelota;
    if (!b || b.holder) return false;
    const toward = b.velocity.x * ownSide(c.me) > 8;
    return toward && dist(b.position, c.me.position) < 14;
  }),
  sequence(
    "defende",
    condition("bola vindo para o aro", (c) => {
      const b = c.w.balls?.pelota;
      if (!b || b.holder) return false;
      return b.velocity.x * ownSide(c.me) > 4 && Math.abs(b.position.x - ownSide(c.me) * PITCH.hoopX) < 30;
    }),
    action("vai na bola", (c) => {
      const b = ballAhead(c, c.p.leadSeconds);
      const side = ownSide(c.me);
      // Fica na área: no máximo a 12 m da linha dos aros.
      const x = side * Math.max(PITCH.hoopX - 12, Math.min(PITCH.hoopX - 1.5, side * b.x));
      go(c, v3(x, Math.max(10, Math.min(22, b.y)), clampZ(b.z, 10)), { turbo: true });
      if (dist(c.w.balls?.pelota.position ?? b, c.me.position) < BALL.keeperReach) tap(c, BUTTON.action);
    }),
  ),
  action("guarda os aros", (c) => {
    const side = ownSide(c.me);
    const mid = goalOf(side);
    if (!c.p.teamwork) return go(c, v3(side * (PITCH.hoopX - 3), mid.y, 0), { arrive: true });
    // Na linha entre a bola e o aro do meio, a 4 m dos aros, na altura e no lado da bola.
    const b = ballAhead(c, 0);
    const t = 4 / Math.max(4, flat(b, mid));
    go(
      c,
      v3(mid.x + (b.x - mid.x) * t, Math.max(12, Math.min(20, b.y)), clampZ(mid.z + (b.z - mid.z) * 0.35, 8)),
      {
        arrive: true,
      },
    );
  }),
);

// ---------------------------------------------------------------- batedor

const BATEDOR: BtNode<Ctx> = selector(
  "batedor",
  ability("Rebatida Dupla", (c) =>
    (c.w.balls?.petardos ?? []).every(
      (p) => dist(p.position, c.me.position) <= ABILITIES.rebatidaDupla.reach * 0.8,
    ),
  ),
  sequence(
    "rebate",
    condition("Petardo no alcance", (c) =>
      (c.w.balls?.petardos ?? []).some(
        (p) => p.mode !== "lancado" && dist(p.position, c.me.position) < BALL.petardo.batReach + 0.5,
      ),
    ),
    action("mira e rebate", (c) => {
      // Alvo: quem está com a bola (se for adversário); senão, o adversário mais perto.
      const h = holderOf(c);
      const prey =
        h && h.team !== c.me.team
          ? h
          : closest(
              opponents(c).filter((o) => o.stunTicks === 0),
              c.me.position,
            );
      // Petardo caçando quase em cima: rebate já, sem tempo de mirar.
      const urgent = (c.w.balls?.petardos ?? []).some(
        (p) => p.mode === "caca" && dist(p.position, c.me.position) < 2.2,
      );
      if (c.p.aimsBat && prey && !urgent && facing(c.me, prey.position) < 0.85) {
        go(c, prey.position);
        return;
      }
      tap(c, BUTTON.action);
    }),
  ),
  sequence(
    "protege o companheiro",
    condition("Petardo caçando um companheiro", (c) => c.p.teamwork && threatenedMate(c) !== null),
    action("vai no Petardo", (c) => {
      const p = threatenedMate(c);
      if (p) go(c, p, { turbo: true });
    }),
  ),
  action("vai no Petardo mais perto", (c) => {
    let best: Vec3 | null = null;
    let bd = Infinity;
    for (const p of c.w.balls?.petardos ?? []) {
      if (p.mode === "lancado") continue;
      const d = dist(p.position, c.me.position);
      if (d < bd) {
        bd = d;
        best = p.position;
      }
    }
    if (best) go(c, best, { turbo: bd > 20 });
    else go(c, v3(ownSide(c.me) * 20, 14, 0), { arrive: true });
  }),
);

/** Petardo caçando cujo atleta mais perto é meu companheiro. */
function threatenedMate(c: Ctx): Vec3 | null {
  const team = [c.me, ...mates(c)];
  for (const p of c.w.balls?.petardos ?? []) {
    if (p.mode !== "caca") continue;
    const near = closest(
      c.w.athletes.filter((a) => a.stunTicks === 0),
      p.position,
    );
    if (near && near.team === c.me.team && near.id !== c.me.id && team.includes(near)) return p.position;
  }
  return null;
}

// ---------------------------------------------------------------- caçador

const CACADOR: BtNode<Ctx> = selector(
  "caçador",
  sequence(
    "Faísca em campo",
    condition("Faísca apareceu", (c) => c.w.balls?.faisca.active === true),
    selector(
      "pegar a Faísca",
      sequence(
        "ao alcance",
        condition("Faísca ao alcance", (c) => {
          const f = c.w.balls?.faisca;
          return !!f && dist(f.position, c.me.position) < FLIGHT.athleteRadius + BALL.faisca.catchReach;
        }),
        action("agarra", (c) => tap(c, BUTTON.action)),
      ),
      ability("Mergulho", (c) => {
        const f = c.w.balls?.faisca;
        if (!f) return false;
        const d = dist(f.position, c.me.position);
        // Só mergulha com altura de sobra: abaixo disso, a queda atordoa.
        return (
          d < 10 &&
          f.position.y < c.me.position.y - 1 &&
          c.me.position.y > 12 &&
          facing(c.me, f.position) > 0.8
        );
      }),
      action("persegue a Faísca", (c) => {
        const f = c.w.balls?.faisca;
        if (!f) return;
        const t = c.p.leadSeconds;
        go(
          c,
          v3(
            f.position.x + f.velocity.x * t,
            f.position.y + f.velocity.y * t,
            f.position.z + f.velocity.z * t,
          ),
          {
            turbo: true,
          },
        );
      }),
    ),
  ),
  action("espera no alto", (c) => go(c, v3(ownSide(c.me) * -5, 26, 0), { arrive: true })),
);

export const TREES: Record<Position, BtNode<Ctx>> = {
  artilheiro: ARTILHEIRO,
  goleiro: GOLEIRO,
  batedor: BATEDOR,
  cacador: CACADOR,
};

/** Usado pelo cérebro: não ficar perto dos próprios aros por tempo demais. */
export const HOOP_BLOCK_SAFE = FOULS.hoopBlockDistance + 1;
